interface EntitySnapshotMergeOptions<T> {
  snapshot: T[];
  current: T[];
  getId: (entity: T) => string;
  mutationRevisionById: ReadonlyMap<string, number>;
  requestStartRevision: number;
  mergeConcurrentMutation?: (snapshotEntity: T, currentEntity: T) => T;
}

interface EntityPageMergeOptions<T> {
  current: T[];
  page: T[];
  getId: (entity: T) => string;
  mutationRevisionById: ReadonlyMap<string, number>;
  requestStartRevision: number;
  mergePageEntity?: (currentEntity: T, pageEntity: T) => T;
}

const defaultMergeConcurrentMutation = <T,>(
  snapshotEntity: T,
  currentEntity: T,
): T => ({
  ...(snapshotEntity as Record<string, unknown>),
  ...(currentEntity as Record<string, unknown>),
} as T);

const defaultMergePageEntity = <T,>(
  currentEntity: T,
  pageEntity: T,
): T => ({
  ...(currentEntity as Record<string, unknown>),
  ...(pageEntity as Record<string, unknown>),
} as T);

/**
 * Publish a REST collection snapshot without erasing entities changed after
 * that request began. Newly observed concurrent entities are prepended in
 * their current order; duplicate snapshot IDs are collapsed deterministically.
 */
export const mergeEntitySnapshotPreservingMutations = <T,>({
  snapshot,
  current,
  getId,
  mutationRevisionById,
  requestStartRevision,
  mergeConcurrentMutation = defaultMergeConcurrentMutation,
}: EntitySnapshotMergeOptions<T>): T[] => {
  const currentById = new Map<string, T>();
  current.forEach((entity) => {
    const id = getId(entity);
    if (id) currentById.set(id, entity);
  });

  const snapshotIds = new Set<string>();
  const mergedSnapshot: T[] = [];
  snapshot.forEach((snapshotEntity) => {
    const id = getId(snapshotEntity);
    if (!id || snapshotIds.has(id)) return;
    snapshotIds.add(id);
    const currentEntity = currentById.get(id);
    const changedDuringRequest = (
      currentEntity
      && (mutationRevisionById.get(id) || 0) > requestStartRevision
    );
    mergedSnapshot.push(
      changedDuringRequest
        ? mergeConcurrentMutation(snapshotEntity, currentEntity)
        : snapshotEntity,
    );
  });

  const concurrentAdditionIds = new Set<string>();
  const concurrentAdditions = current.filter((entity) => {
    const id = getId(entity);
    const shouldInclude = Boolean(
      id
      && !snapshotIds.has(id)
      && !concurrentAdditionIds.has(id)
      && (mutationRevisionById.get(id) || 0) > requestStartRevision,
    );
    if (shouldInclude) concurrentAdditionIds.add(id);
    return shouldInclude;
  });
  return [...concurrentAdditions, ...mergedSnapshot];
};

/** Append a cursor page by ID, refreshing overlaps without creating tiles twice. */
export const mergeEntityPagePreservingMutations = <T,>({
  current,
  page,
  getId,
  mutationRevisionById,
  requestStartRevision,
  mergePageEntity = defaultMergePageEntity,
}: EntityPageMergeOptions<T>): T[] => {
  const result: T[] = [];
  const indexById = new Map<string, number>();

  current.forEach((entity) => {
    const id = getId(entity);
    if (!id || indexById.has(id)) return;
    indexById.set(id, result.length);
    result.push(entity);
  });

  page.forEach((pageEntity) => {
    const id = getId(pageEntity);
    if (!id) return;
    const existingIndex = indexById.get(id);
    if (existingIndex === undefined) {
      indexById.set(id, result.length);
      result.push(pageEntity);
      return;
    }
    if ((mutationRevisionById.get(id) || 0) > requestStartRevision) {
      return;
    }
    result[existingIndex] = mergePageEntity(result[existingIndex], pageEntity);
  });

  return result;
};
